import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/react';
import { CONTEXTUAL_SECTIONS } from '../config/contextual-onboarding-registry.js';
import { useOnboardingState } from './use-onboarding-state.js';
import { useApiClient } from './use-api-client.js';

function getLocalStorageKey(userId: string, sectionId: string): string {
  return `contextual_onboard:${userId}:${sectionId}`;
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Best-effort — private browsing or full storage
  }
}

export interface UseContextualOnboardingResult {
  active: boolean;
  currentStep: number;
  totalSteps: number;
  targetRect: DOMRect | null;
  dismiss: () => void;
  next: () => void;
}

const MEASURE_RETRY_DELAY = 100;
const MEASURE_MAX_RETRIES = 10;

export function useContextualOnboarding(sectionId: string): UseContextualOnboardingResult {
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [checkedKv, setCheckedKv] = useState(false);
  const { fetchApi } = useApiClient();
  const { userId } = useAuth();
  const { data: onboardingState, isLoading: onboardingLoading } = useOnboardingState();
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const section = CONTEXTUAL_SECTIONS.find((s) => s.sectionId === sectionId);
  const totalSteps = section?.steps.length ?? 0;

  // Check if dismissed in localStorage or KV
  useEffect(() => {
    if (onboardingLoading || !onboardingState?.completed || !userId) {
      return;
    }

    const localKey = getLocalStorageKey(userId, sectionId);
    const localValue = safeGetItem(localKey);

    if (localValue === 'done') {
      setActive(false);
      setCheckedKv(true);
      return;
    }

    // Check KV (cold cache)
    let cancelled = false;
    fetchApi(`/v1/onboard/contextual?section=${encodeURIComponent(sectionId)}`)
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          const body = await res.json();
          if (body.data?.dismissed) {
            safeSetItem(localKey, 'done');
            setActive(false);
          } else {
            setActive(true);
          }
        } else {
          // API error — show onboarding (fail-open for UX)
          setActive(true);
        }
        setCheckedKv(true);
      })
      .catch(() => {
        if (!cancelled) {
          setActive(true);
          setCheckedKv(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sectionId, userId, fetchApi, onboardingLoading, onboardingState?.completed]);

  // Measure target element
  const measureTarget = useCallback(() => {
    if (!section || !active || !checkedKv) {
      setTargetRect(null);
      return;
    }

    const step = section.steps[currentStep];
    if (!step) {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(step.targetSelector);
    if (el) {
      setTargetRect(el.getBoundingClientRect());
    } else {
      setTargetRect(null);
    }
  }, [section, active, checkedKv, currentStep]);

  useEffect(() => {
    measureTarget();
  }, [measureTarget]);

  // Retry measurement for async-loaded target elements (P2)
  useEffect(() => {
    if (!active || !checkedKv || targetRect !== null) return;
    if (!section) return;
    const step = section.steps[currentStep];
    if (!step) return;

    let retries = 0;
    const timer = setInterval(() => {
      const el = document.querySelector(step.targetSelector);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
        clearInterval(timer);
      } else if (++retries >= MEASURE_MAX_RETRIES) {
        clearInterval(timer);
      }
    }, MEASURE_RETRY_DELAY);

    return () => clearInterval(timer);
  }, [active, checkedKv, targetRect, section, currentStep]);

  // Re-measure on resize and scroll (debounced 200ms)
  useEffect(() => {
    if (!active) return;

    function handleEvent() {
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = setTimeout(() => {
        measureTarget();
      }, 200);
    }

    window.addEventListener('resize', handleEvent);
    window.addEventListener('scroll', handleEvent, true);
    return () => {
      window.removeEventListener('resize', handleEvent);
      window.removeEventListener('scroll', handleEvent, true);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
    };
  }, [active, measureTarget]);

  const dismiss = useCallback(() => {
    if (!userId) return;
    const localKey = getLocalStorageKey(userId, sectionId);
    safeSetItem(localKey, 'done');
    setActive(false);

    // Fire-and-forget KV persist
    fetchApi('/v1/onboard/contextual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ section: sectionId }),
    }).catch(() => {
      // Best-effort — localStorage is primary
    });
  }, [sectionId, userId, fetchApi]);

  const next = useCallback(() => {
    setCurrentStep((prev) => (prev < totalSteps - 1 ? prev + 1 : prev));
  }, [totalSteps]);

  // If onboarding is not yet complete or loading, don't activate
  if (onboardingLoading || !onboardingState?.completed) {
    return { active: false, currentStep: 0, totalSteps, targetRect: null, dismiss, next };
  }

  return { active, currentStep, totalSteps, targetRect, dismiss, next };
}
